import {ReactElement, useEffect, useState} from 'react'
import {Button, Card, CardContent, Grid, Typography} from '@material-ui/core'
import {useHistory} from 'react-router-dom'
import {Loading} from '../../Common/Loading'
import PageBody from '../../Common/PageBody'
import {useQueryApiV2} from '../../../shared/hooks/use-query-api-v2'
import {recordPromotionEvent} from './api'
import {CurrentPromotion, PromotionOffer} from './types'
import {PromotionOpenDialog} from './PromotionOpenDialog'
import {PromotionRulesDialog} from './PromotionRulesDialog'
import {promotionFactorBadgeTone} from './promotionOpenHelpers'
import {
  highlightPromotionTitleParts,
  PROMOTION_EMPTY_BODY_COUNTRY,
  PROMOTION_EMPTY_TITLE,
  PROMOTION_HERO_READ_RULES_LABEL,
  PROMOTION_HOW_IT_WORKS,
  PROMOTION_OPEN_ACCOUNT_CTA,
  PROMOTION_READY_READ_RULES_LABEL,
  PROMOTION_RULES_SHORT,
} from './promotionCopy'
import './tenx-promotion.css'

type OpenTarget = {multiplier: number; tierName: string} | null

export function PromotionPage(): ReactElement {
  const history = useHistory()
  const [promotion, {loading, error}] = useQueryApiV2<CurrentPromotion | null>({path: '/dashboard/promotions/current'})
  const [selectedMultiplier, setSelectedMultiplier] = useState<number>()
  const [selectedDeposit, setSelectedDeposit] = useState<number>()
  const [rulesOpen, setRulesOpen] = useState(false)
  const [openTarget, setOpenTarget] = useState<OpenTarget>(null)
  useEffect(() => {
    const offer = promotion?.offers[0]
    if (!offer) return
    setSelectedMultiplier((current) => current ?? offer.multiplier)
    setSelectedDeposit((current) => current ?? offer.tiers[0]?.deposit)
  }, [promotion])
  if (loading) return <Loading />
  if (error || !promotion) {
    return (
      <div className="pm-empty" data-testid="pm-unavailable">
        <Typography variant="h5" component="h2" gutterBottom>
          {PROMOTION_EMPTY_TITLE}
        </Typography>
        <Typography color="textSecondary" paragraph>
          {PROMOTION_EMPTY_BODY_COUNTRY}
        </Typography>
        <div className="pm-empty__actions">
          <Button className="pm-btn pm-btn--blue" variant="contained" disableElevation onClick={() => history.push('/accounts')}>
            Back to accounts
          </Button>
          <Button className="pm-btn--outline" variant="outlined" onClick={() => history.push('/support')}>
            Contact support
          </Button>
        </div>
      </div>
    )
  }
  const {campaign, offers, maxMultiplier} = promotion
  const {display} = campaign.rules.promotion
  const selectedOffer = offers.find((offer) => offer.multiplier === selectedMultiplier) ?? offers[0]
  const selectedTier = selectedOffer.tiers.find((tier) => tier.deposit === selectedDeposit) ?? selectedOffer.tiers[0]
  const until = `Until ${new Date(campaign.end).toLocaleDateString()}`

  const openTier = (offer: PromotionOffer, tierName: string): void => {
    recordPromotionEvent(campaign.id, {event: 'tier_open_click', multiplier: offer.multiplier, tier: tierName}).catch(() => undefined)
    setOpenTarget({multiplier: offer.multiplier, tierName})
  }

  return (
    <PageBody>
      <Button color="default" onClick={() => history.push('/accounts')} style={{marginBottom: 12}}>
        ← Accounts
      </Button>

      <section className="pm-hero" data-testid="pm-hero">
        <span className="pm-hero__pill">Tauro 10x · Classic accounts · up to {maxMultiplier}x</span>
        {/* Native elements so MUI theme color cannot override hero contrast */}
        <h1 className="pm-hero__title">
          {highlightPromotionTitleParts(display.pageHeadline).map((part, index) =>
            part.type === 'money' ? (
              <em key={index}>{part.value}</em>
            ) : (
              <span key={index}>{part.value}</span>
            ),
          )}
        </h1>
        <p className="pm-hero__text">{display.pageText}</p>
        <div className="pm-hero__actions">
          <Button
            className="pm-btn"
            variant="contained"
            disableElevation
            onClick={() => document.getElementById('pm-tiers')?.scrollIntoView({behavior: 'smooth'})}
          >
            Pick your tier ↓
          </Button>
          <Button
            className="pm-btn pm-btn--light"
            variant="outlined"
            disableElevation
            onClick={() => setRulesOpen(true)}
          >
            {PROMOTION_HERO_READ_RULES_LABEL}
          </Button>
        </div>
        <ul className="pm-hero__meta">
          <li>{until}</li>
          <li>You can never lose more than your deposits</li>
          <li>Leverage fixed per Factor</li>
        </ul>
      </section>

      <section className="pm-card-shell" id="pm-tiers">
        <div className="pm-card-shell__head">
          <h2>Pick your tier</h2>
          <span className="pm-until">{until}</span>
        </div>
        <p className="pm-card-shell__sub">
          All tiers are MT5 Classic accounts. The trading balance is your deposit x the Factor; the leverage is fixed per
          Factor. Pick a card to open that account.
        </p>
        {offers.map((offer) => (
          <div className="pm-group" key={offer.multiplier}>
            <div className="pm-group__head">
              <h3>
                <span className="pm-factor-badge" style={{backgroundColor: promotionFactorBadgeTone(offer.multiplier).bg}}>
                  {promotionFactorBadgeTone(offer.multiplier).label}
                </span>
                {offer.multiplier}x factorisation
              </h3>
              <ul className="pm-group__terms">
                <li>
                  Drawdown <b>{offer.drawdownTriggerPercent}%</b>
                </li>
                <li>
                  Profit split (you / Tauro){' '}
                  <b>
                    {offer.clientProfitSplitPercent} / {offer.tauroProfitSplitPercent}
                  </b>
                </li>
                <li>
                  Leverage <b>1:{offer.leverage}</b>
                </li>
                <li>
                  Deposit{' '}
                  <b>
                    {offer.minQualifyingDeposit.toLocaleString()} to {offer.maxQualifyingDeposit.toLocaleString()} USD
                  </b>
                </li>
              </ul>
            </div>
            <Grid container spacing={2}>
              {offer.tiers.map((tier) => {
                const tone = promotionFactorBadgeTone(offer.multiplier)
                return (
                  <Grid item xs={12} sm={6} md={3} key={`${offer.multiplier}:${tier.name}`}>
                    <Card
                      className={`pm-card${offer.canOpen ? '' : ' pm-card--closed'}`}
                      elevation={0}
                      data-testid={offer.canOpen ? 'pm-tier-open' : 'pm-tier-closed'}
                    >
                      <CardContent>
                        <span className="pm-card__factor" style={{backgroundColor: tone.bg}}>
                          {tone.label}
                        </span>
                        <Typography className="pm-card__name">{tier.name}</Typography>
                        <Typography className="pm-card__balance">${tier.tradingBalance.toLocaleString()}</Typography>
                        <Typography className="pm-card__sub">
                          trading balance from ${tier.deposit.toLocaleString()} deposit
                        </Typography>
                        <ul className="pm-card__features">
                          <li>1:{offer.leverage} leverage</li>
                          <li>Closes at equity of ${tier.closesAt.toLocaleString()}</li>
                          <li>
                            {offer.clientProfitSplitPercent} / {offer.tauroProfitSplitPercent} profit split
                          </li>
                        </ul>
                        <Button
                          className="pm-card__cta"
                          disableElevation
                          disabled={!offer.canOpen}
                          onClick={() => openTier(offer, tier.name)}
                        >
                          {offer.canOpen ? 'Open this account →' : 'Closed to new accounts →'}
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                )
              })}
            </Grid>
          </div>
        ))}
      </section>

      <Typography className="pm-section-title" variant="h5" component="h2">
        See what your deposit does
      </Typography>
      <div className="pm-calc" data-testid="pm-calc">
        <p className="pm-calc__label">Factor</p>
        <div className="pm-calc__chips">
          {offers.map((offer) => (
            <button
              key={offer.multiplier}
              type="button"
              className={`pm-calc__chip${selectedOffer.multiplier === offer.multiplier ? ' pm-calc__chip--active' : ''}${
                offer.canOpen ? '' : ' pm-calc__chip--closed'
              }`}
              disabled={!offer.canOpen && selectedOffer.multiplier !== offer.multiplier}
              onClick={() => {
                setSelectedMultiplier(offer.multiplier)
                setSelectedDeposit(offer.tiers[0]?.deposit)
              }}
            >
              {offer.multiplier}x
            </button>
          ))}
        </div>
        <p className="pm-calc__label">Deposit</p>
        <div className="pm-calc__chips">
          {selectedOffer.tiers.map((tier) => (
            <button
              key={tier.deposit}
              type="button"
              className={`pm-calc__chip pm-calc__chip--tile${
                selectedTier.deposit === tier.deposit ? ' pm-calc__chip--active' : ''
              }`}
              onClick={() => setSelectedDeposit(tier.deposit)}
            >
              ${tier.deposit.toLocaleString()}
            </button>
          ))}
        </div>
        <dl className="pm-calc__results">
          <div>
            <dt>Your deposit</dt>
            <dd>${selectedTier.deposit.toLocaleString()}</dd>
          </div>
          <div>
            <dt>Starting balance ({selectedOffer.multiplier}x)</dt>
            <dd className="pm-calc__emphasis">${selectedTier.tradingBalance.toLocaleString()}</dd>
          </div>
          <div>
            <dt>Account closes at equity of</dt>
            <dd>${selectedTier.closesAt.toLocaleString()}</dd>
          </div>
          <div>
            <dt>The most you can lose</dt>
            <dd>${selectedTier.deposit.toLocaleString()}</dd>
          </div>
        </dl>
        <p className="pm-calc__hint">
          Starting balance = deposit × {selectedOffer.multiplier}. The account closes when your equity falls by{' '}
          {selectedOffer.drawdownTriggerPercent}% of the starting balance. Profit above the starting balance is split{' '}
          {selectedOffer.clientProfitSplitPercent} / {selectedOffer.tauroProfitSplitPercent} at every profit withdrawal;
          the account keeps running. Leverage 1:{selectedOffer.leverage}, fixed.
        </p>
      </div>

      <section className="pm-card-shell">
        <div className="pm-card-shell__head">
          <h2>How it works</h2>
        </div>
        <ol className="pm-steps">
          {PROMOTION_HOW_IT_WORKS.map((step, index) => (
            <li key={step.title}>
              <span className="pm-step-num">{index + 1}</span>
              <b>{step.title}</b>
              <span>{step.text}</span>
            </li>
          ))}
        </ol>
      </section>

      <section className="pm-card-shell" id="pm-rules">
        <div className="pm-card-shell__head">
          <h2>The rules in short</h2>
        </div>
        <ul className="pm-rules">
          {PROMOTION_RULES_SHORT.map((rule) => (
            <li key={rule}>{rule}</li>
          ))}
        </ul>
      </section>

      <div className="pm-cta-bar">
        <div>
          <h2>Ready?</h2>
          <p>
            Pick your tier above, or start with {selectedOffer.multiplier}x. The offer runs until{' '}
            {new Date(campaign.end).toLocaleDateString()}.
          </p>
        </div>
        <div className="pm-cta-bar__actions">
          <Button className="pm-btn--outline" variant="outlined" onClick={() => setRulesOpen(true)}>
            {PROMOTION_READY_READ_RULES_LABEL}
          </Button>
          <Button
            className="pm-btn"
            variant="contained"
            disableElevation
            disabled={!selectedOffer.canOpen}
            onClick={() => openTier(selectedOffer, selectedTier.name)}
          >
            {PROMOTION_OPEN_ACCOUNT_CTA}
          </Button>
        </div>
      </div>

      {openTarget && (
        <PromotionOpenDialog
          open
          onClose={() => setOpenTarget(null)}
          onOpenRules={() => setRulesOpen(true)}
          promotion={promotion}
          initialMultiplier={openTarget.multiplier}
          initialTierName={openTarget.tierName}
        />
      )}
      {/* After open dialog + zIndex 1400 so rules never hide behind it (PT-8340) */}
      <PromotionRulesDialog open={rulesOpen} onClose={() => setRulesOpen(false)} offers={offers} />
    </PageBody>
  )
}
